import { Box, Container } from "@mui/material";
import dayjs from "dayjs";
import { useFetchDetail, usePutDetail } from "@hooks/useFetchDetail";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeroCst from "@components/HeroCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { useAddEditPhysicalEdition } from "../hooks/useAddEditPhysicalEdition";
import AnimationFadeIn from "@components/AnimationFadeIn";
import EditionFormCst from "@components/EditionFormCst";
import { useAuth } from "@context/auth";
import { useState, useMemo, useEffect } from "react";
import { useGetEnrollmentStatus } from "@hooks/useGetEnrollmentStatus";

const EditPhysicalEditions: React.FC = () => {
  const { t } = useTranslation(["physical-editions-page"]);
  const { user } = useAuth();
  const { idEdition, id } = useParams();
  const [activityDuration, setActivityDuration] = useState(0);
  const [showModalWarning, setShowModalWarning] = useState(false);

  const readOnly =
    (user?.permissions.includes("editions.physical.retrieve") &&
      !user?.permissions.includes("editions.physical.update")) ||
    false;

  const {
    columns,
    dataDetail,
    mutate,
    isLoading: isLoadingDetail,
  } = useFetchDetail(
    ServicesURL.getEditionsDetail + `/physical`,
    idEdition + `?corporateId=${id}` || "",
    PagesID["corporate.physical-editions"],
  );

  const {
    dataDetail: sessionDetail,
    isLoading: isLoadingSession,
    mutate: mutateSession,
  } = useFetchDetail(
    ServicesURL.getEditions + `/physicalSessions`,
    idEdition + `?corporateId=${id}` || "",
    PagesID["corporate.physical-editions"],
  );
  const navigate = useNavigate();

  const { trigger } = usePutDetail(
    ServicesURL.getEditions + `/physical/${idEdition}?corporateId=${id}`,
    { onError: () => {} },
  );

  const { columns: sessionColumns } = useFetchDetail(
    ServicesURL.getEditions,
    undefined,
    PagesID["corporate.physical-editions.add"],
  );

  const { enrollmentStatus } = useGetEnrollmentStatus(idEdition, true);

  const confirmedDisabledTooltip = useMemo(() => {
    if (enrollmentStatus && !enrollmentStatus.allEnrollmentsCompleted)
      return t("enrollment-warning.meeting-disabled");

    return undefined;
  }, [enrollmentStatus, t]);

  const { dataDetail: activityDetail } = useFetchDetail(
    dataDetail.learning_objects?.id
      ? `${ServicesURL.getActivities.replace(":idCorporate", `${id}`)}`
      : "",
    dataDetail.learning_objects?.id || "",
    PagesID["catalog.activities"],
  );

  useEffect(() => {
    setActivityDuration(activityDetail?.duration);
  }, [activityDetail]);

  const {
    onSubmit,
    openConfirmStatus,
    setOpenConfirmStatus,
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetSessionAssociations,
    setSiteAssociation,
    setRoomAssociation,
    setFacultiesAssociation,
    setTutorsAssociation,
    siteAssociation,
    facultiesAssociation,
    roomAssociation,
    tutorsAssociation,
    openAssociations,
    hasDifferentDuration,
    savedInputForm,
    savedSessions,
  } = useAddEditPhysicalEdition(
    trigger,
    id,
    dataDetail,
    mutate,
    activityDuration,
    mutateSession,
  );

  const inputsException = (col: any, inputForm: any) => {
    if (Object.keys(dataDetail).length > 0) {
      if (
        dataDetail &&
        Object.keys(dataDetail).length > 0 &&
        columns?.length > 0 &&
        dayjs().isAfter(dayjs(dataDetail["publish_date"]))
      ) {
        if (
          !["edition_code", "edition_name", "extra_info"].includes(
            col.accessorKey,
          )
        ) {
          col.disabled = true;
        }
      } else {
        if (
          !["edition_code", "edition_name", "extra_info"].includes(
            col.accessorKey,
          )
        ) {
          col.disabled = false;
        }
        if (siteAssociation && siteAssociation.length === 0) {
          if (["rooms"].includes(col.accessorKey)) {
            col.disabled = true;
          }
        } else {
          if (["rooms"].includes(col.accessorKey)) {
            col.disabled = false;
          }
        }
      }
    }
    return col;
  };

  useEffect(() => {
    if (hasDifferentDuration) {
      setShowModalWarning(true);
    }
  }, [hasDifferentDuration]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-editions-physical.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("edit.hero-title")}
          description={t("edit.hero-description")}
        />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            {!isLoadingDetail && !isLoadingSession && (
              <EditionFormCst
                row={{ ...dataDetail, ...sessionDetail }}
                columns={columns}
                inputsException={inputsException}
                sessionColumns={sessionColumns}
                onSubmit={onSubmit}
                backButton={() => {
                  navigate(-1);
                }}
                setOpenAssociation={(accessorKey, open) =>
                  setOpenAssociation(accessorKey, open)
                }
                handleDelete={(accessorKey, el) =>
                  handleDelete(accessorKey, el)
                }
                handleAssociation={(accessorKey, value) =>
                  handleAssociation(accessorKey, value)
                }
                getValues={(accessorKey) => getValues(accessorKey)}
                association={openAssociations}
                resetSessionAssociations={resetSessionAssociations}
                siteAssociation={siteAssociation}
                facultiesAssociation={facultiesAssociation}
                roomAssociation={roomAssociation}
                tutorsAssociation={tutorsAssociation}
                setSiteAssociation={setSiteAssociation}
                setRoomAssociation={setRoomAssociation}
                setFacultiesAssociation={setFacultiesAssociation}
                setTutorsAssociation={setTutorsAssociation}
                setActivityDuration={setActivityDuration}
                readOnly={readOnly}
                confirmedDisabledTooltip={confirmedDisabledTooltip}
              />
            )}
          </Box>
        )}
        <ModalConfirmsCst
          open={!!openConfirmStatus}
          title={t("outcome-item", { ns: "modal" })}
          description={t("outcome-item", { ns: "modal" })}
          onCallBackClosed={() => setOpenConfirmStatus(undefined)}
          status={openConfirmStatus}
        />
        <ModalConfirmsCst
          open={showModalWarning}
          title={t("duration-warning.title")}
          description={t("duration-warning.description")}
          routeToSamePage={true}
          onActionConfirmed={() => {
            setShowModalWarning(false);
            savedSessions && onSubmit(savedInputForm, savedSessions, true);
          }}
          onCallBackClosed={() => {
            setShowModalWarning(false);
          }}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default EditPhysicalEditions;
